import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X as XIcon, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { api } from "../../lib/api";

/** Form-side: upload photos (multi-select / drag-drop) and manage the draft's `photos` list. */
export function FilmPhotoUploader({ playerId, photos, onChange, testPrefix }) {
  const inputRef = useRef(null);
  const [uploads, setUploads] = useState([]); // [{key, name, pct}]
  const [drag, setDrag] = useState(false);

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(f => f.type.startsWith("image/"));
    if (!list.length) { toast.error("Photos only (JPG, PNG, WebP, GIF, HEIC)"); return; }
    for (const f of list) {
      const key = `${f.name}-${Date.now()}-${Math.random()}`;
      setUploads(u => [...u, { key, name: f.name, pct: 0 }]);
      try {
        const meta = await api.uploadFilmPhoto(playerId, f, pct => setUploads(u => u.map(x => x.key === key ? { ...x, pct } : x)));
        onChange(prev => [...(prev || []), meta]);
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`, { description: e?.response?.data?.detail || e.message });
      } finally {
        setUploads(u => u.filter(x => x.key !== key));
      }
    }
  };

  return (
    <div data-testid={`film-photos-${testPrefix}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Photos <span className="text-[#575757] normal-case tracking-normal">· screenshots, stills, whiteboard</span></div>
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`border border-dashed p-3 flex flex-wrap gap-2 items-center transition-colors ${drag ? "border-[var(--org-accent)] bg-[var(--org-accent)]/5" : "border-white/15 bg-[#101010]"}`}
      >
        {(photos || []).map(ph => (
          <div key={ph.id} className="relative w-20 h-20 group" data-testid={`film-photo-thumb-${testPrefix}-${ph.id}`}>
            <img src={api.filmPhotoUrl(playerId, ph.id)} alt={ph.name} className="w-full h-full object-cover border border-white/10" />
            <button type="button" onClick={() => onChange(prev => (prev || []).filter(p => p.id !== ph.id))} title="Remove photo"
              className="absolute -top-1.5 -right-1.5 bg-[#141414] border border-white/20 text-[#a0a0a0] hover:text-red-300 hover:border-red-500/40 p-0.5" data-testid={`film-photo-remove-${testPrefix}-${ph.id}`}>
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
        {uploads.map(u => (
          <div key={u.key} className="w-20 h-20 border border-white/10 bg-[#141414] flex flex-col items-center justify-center gap-1 text-[#a0a0a0]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /><span className="text-[9px] font-mono">{u.pct}%</span>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()}
          className="w-20 h-20 border border-white/15 flex flex-col items-center justify-center gap-1 text-[#a0a0a0] hover:text-white hover:border-[var(--org-accent)] text-[9px] uppercase tracking-widest"
          data-testid={`film-photo-add-${testPrefix}`}>
          <ImagePlus className="w-4 h-4" /> Add
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ""; }} data-testid={`film-photo-input-${testPrefix}`} />
        {!(photos || []).length && !uploads.length && <span className="text-[11px] text-[#575757] ml-1">Drop images here or click Add</span>}
      </div>
    </div>
  );
}

/** Row-side: thumbnail strip with a lightbox (arrows / keyboard). */
export function FilmPhotoGallery({ playerId, photos, rowId }) {
  const [idx, setIdx] = useState(null);
  if (!photos?.length) return null;
  const open = idx !== null;
  const step = (d) => setIdx(i => (i + d + photos.length) % photos.length);
  return (
    <div className="mt-3" data-testid={`film-gallery-${rowId}`}>
      <div className="flex flex-wrap gap-2">
        {photos.map((ph, i) => (
          <button key={ph.id} type="button" onClick={() => setIdx(i)} className="w-24 h-24 sm:w-28 sm:h-28 border border-white/10 hover:border-[var(--org-accent)] overflow-hidden" data-testid={`film-gallery-thumb-${rowId}-${ph.id}`}>
            <img src={api.filmPhotoUrl(playerId, ph.id)} alt={ph.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
          </button>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center" onClick={() => setIdx(null)} onKeyDown={e => { if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); if (e.key === "Escape") setIdx(null); }} tabIndex={-1} ref={el => el?.focus()} data-testid={`film-lightbox-${rowId}`}>
          <button className="absolute top-4 right-4 p-2 text-[#a0a0a0] hover:text-white" onClick={() => setIdx(null)} aria-label="Close" data-testid="film-lightbox-close"><XIcon className="w-5 h-5" /></button>
          <a href={api.filmPhotoUrl(playerId, photos[idx].id, true)} onClick={e => e.stopPropagation()} className="absolute top-4 right-14 p-2 text-[#a0a0a0] hover:text-white" aria-label="Download" data-testid="film-lightbox-download"><Download className="w-5 h-5" /></a>
          {photos.length > 1 && <button className="absolute left-3 p-2 text-[#a0a0a0] hover:text-white" onClick={e => { e.stopPropagation(); step(-1); }} aria-label="Previous"><ChevronLeft className="w-7 h-7" /></button>}
          <img src={api.filmPhotoUrl(playerId, photos[idx].id)} alt={photos[idx].name} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={e => e.stopPropagation()} />
          {photos.length > 1 && <button className="absolute right-3 p-2 text-[#a0a0a0] hover:text-white" onClick={e => { e.stopPropagation(); step(1); }} aria-label="Next"><ChevronRight className="w-7 h-7" /></button>}
          <div className="absolute bottom-4 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">{idx + 1} / {photos.length} · {photos[idx].name}</div>
        </div>
      )}
    </div>
  );
}
